import axios, { AxiosError } from 'axios';
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { TokenManager } from './token-manager';
import { checkSessionValidity, performAutoLogin } from './auth-utils';
import { MFE_AXIOS_TIMEOUT } from './config';

const createAxiosInstance = () =>
  axios.create({
    timeout: MFE_AXIOS_TIMEOUT,
    headers: { 'Content-Type': 'application/json' },
  });

export class NSPortalAxiosInterceptor {
  private tokenManager = new TokenManager();

  constructor(private baseAxios = createAxiosInstance()) {
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.baseAxios.interceptors.request.use(
      config => this.handleRequest(config),
      error => Promise.reject(error)
    );

    this.baseAxios.interceptors.response.use(
      response => response,
      error => this.handleResponseError(error)
    );
  }

  private async handleRequest(
    config: InternalAxiosRequestConfig & { clientId?: string }
  ) {
    const clientId = config.clientId;
    if (!clientId) {
      return config;
    }

    try {
      let token = this.tokenManager.getToken(clientId);

      if (!token) {
        token = await this.tokenManager.generateToken(clientId);
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token.accessToken}`;
        config.headers['X-Client-Id'] = clientId;
      }

      return config;
    } catch {
      // Assumir que o token não está disponível, mas não quebrar a aplicação
      return config;
    }
  }

  private async handleResponseError(
    error: AxiosError
  ): Promise<AxiosResponse | AxiosError> {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      clientId?: string;
      _retry?: boolean;
    };

    if (error.response?.status === 401) {
      if (originalRequest?.clientId && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const sessionValid = await checkSessionValidity();

          if (sessionValid) {
            this.tokenManager.removeToken(originalRequest.clientId);
            const newToken = await this.tokenManager.generateToken(
              originalRequest.clientId
            );

            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken.accessToken}`;
              originalRequest.headers['X-Client-Id'] = originalRequest.clientId;
              return this.baseAxios(originalRequest);
            }
          }
        } catch (refreshError) {
          console.error('💥 [NSPortal] Erro ao renovar token:', refreshError);
        }

        this.handleSessionExpired();
      }
    }

    return Promise.reject(error);
  }

  private handleSessionExpired(): void {
    this.tokenManager.clearAll();
    performAutoLogin();
  }

  clearAllTokens(): void {
    this.tokenManager.clearAll();
  }

  createClientInstance(clientId: string): AxiosInstance {
    const clientAxios = createAxiosInstance();

    clientAxios.interceptors.request.use(
      async config => {
        config.clientId = clientId;
        return this.handleRequest(config);
      },
      error => Promise.reject(error)
    );

    clientAxios.interceptors.response.use(
      response => response,
      error => this.handleResponseError(error)
    );

    return clientAxios;
  }
}
